import React, { useContext, useEffect } from 'react';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { REPORTS_PATHS } from '../../../../Routes/paths';
import styles from '../css/summaryreports.module.scss';
import { usePermission } from 'context/PermissionContext';
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from 'common/constants/PermissionConstants';
import { useGlobalStudy } from 'context/StudyContext';
import NoStudySelectedPlaceholder from '../../../../Components/Common/NoStudySelectedPlaceholder';
import { BreadcrumbContext } from 'Layouts/Header';

const SummaryReports = () => {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const { selectedStudy: globalStudy } = useGlobalStudy();
    const breadcrumbContext = useContext(BreadcrumbContext);

    useEffect(() => {
        breadcrumbContext?.setBreadcrumbItems?.([
            { text: "Pharmacy Reports" }
        ]);
        return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }, []);
    return (
        <div className="page-content">
                {!globalStudy?.studyid ? (
                    <div>
                        <NoStudySelectedPlaceholder moduleName="summary reports" />
                    </div>
                ) : (
                    <>
                    <Container fluid className="px-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h4 className="mb-1 fw-bold text-dark fs-20">Summary Reports</h4>
                                <p className="text-muted mb-0 fs-13">Generate and export study and pharmacy stock status reports</p>
                            </div>
                        </div>

                        <Row className="g-4">
                    {/* IP Stock Summary Card */}
                    {hasPermission(PERMISSION_MODULES.SUMMARY_REPORTS,PERMISSION_ACTIONS.STOCK_SUMMARY_REPORT) && (
                    <Col xl={3} md={6}>
                        <Card className={`${styles.reportCard} ${styles.primaryCard} border-0 h-100`}>
                            <CardBody className="p-4 d-flex flex-column justify-content-between">
                                <div className="position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-4">
                                        <div className={styles.iconContainer}>
                                            <i className="ri-layout-grid-line"></i>
                                        </div>
                                        <div className={styles.watermark}>
                                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="3" y="12" width="4" height="8" rx="1" fill="currentColor" fillOpacity="0.8"/>
                                                <rect x="10" y="8" width="4" height="12" rx="1" fill="currentColor" fillOpacity="0.8"/>
                                                <rect x="17" y="4" width="4" height="16" rx="1" fill="currentColor" fillOpacity="0.8"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h5 className={styles.cardTitle}>IP Stock Summary</h5>
                                    <p className={styles.cardDesc}>
                                        Overview of current stock levels across all locations.
                                    </p>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div 
                                        className={styles.actionLink}
                                        onClick={() => navigate(REPORTS_PATHS.STOCK_SUMMARY)}
                                    >
                                        <i className="ri-file-text-line fs-15 me-1"></i> Generate Report
                                    </div>
                                    <div 
                                        className={styles.arrowCircle}
                                        onClick={() => navigate(REPORTS_PATHS.STOCK_SUMMARY)}
                                    >
                                        <i className="ri-arrow-right-line"></i>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    )}
                    
                    {/* Stock Ledger Card */}
                    {hasPermission(PERMISSION_MODULES.SUMMARY_REPORTS,PERMISSION_ACTIONS.STOCK_LEDGER_REPORT) && (
                    <Col xl={3} md={6}>
                        <Card className={`${styles.reportCard} ${styles.successCard} border-0 h-100`}>
                            <CardBody className="p-4 d-flex flex-column justify-content-between">
                                <div className="position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-4">
                                        <div className={styles.iconContainer}>
                                            <i className="ri-contacts-book-line"></i>
                                        </div>
                                        <div className={styles.watermark}>
                                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="1.5"/>
                                                <path d="M7 7H17M7 12H17M7 17H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h5 className={styles.cardTitle}>Stock Ledger</h5>
                                    <p className={styles.cardDesc}>
                                        Summary of all stock movements, additions, and subtractions.
                                    </p>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div 
                                        className={styles.actionLink}
                                        onClick={() => navigate(REPORTS_PATHS.STOCK_LEDGER)}
                                    >
                                        <i className="ri-file-text-line fs-15 me-1"></i> Generate Report
                                    </div>
                                    <div 
                                        className={styles.arrowCircle}
                                        onClick={() => navigate(REPORTS_PATHS.STOCK_LEDGER)}
                                    >
                                        <i className="ri-arrow-right-line"></i>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    )}
                    
                    {/* Expiring Soon Card */}
                    {hasPermission(PERMISSION_MODULES.SUMMARY_REPORTS,PERMISSION_ACTIONS.STOCK_EXPIRY_SOON_REPORT) && (
                    <Col xl={3} md={6}>
                        <Card className={`${styles.reportCard} ${styles.dangerCard} border-0 h-100`}>
                            <CardBody className="p-4 d-flex flex-column justify-content-between">
                                <div className="position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-4">
                                        <div className={styles.iconContainer}>
                                            <i className="ri-calendar-todo-line"></i>
                                        </div>
                                        <div className={styles.watermark}>
                                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                                                <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
                                                <circle cx="16" cy="15" r="4" stroke="currentColor" strokeWidth="1.5"/>
                                                <path d="M16 13V15H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h5 className={styles.cardTitle}>Expiring Soon</h5>
                                    <p className={styles.cardDesc}>
                                        Identify chemicals and reagents nearing their expiration dates.
                                    </p>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div 
                                        className={styles.actionLink}
                                        onClick={() => navigate(REPORTS_PATHS.EXPIRING_SOON)}
                                    >
                                        <i className="ri-file-text-line fs-15 me-1"></i> Generate Report
                                    </div>
                                    <div 
                                        className={styles.arrowCircle}
                                        onClick={() => navigate(REPORTS_PATHS.EXPIRING_SOON)}
                                    >
                                        <i className="ri-arrow-right-line"></i>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    )}

                    {/* Subject Wise Report Card */}
                    {hasPermission(PERMISSION_MODULES.SUMMARY_REPORTS,PERMISSION_ACTIONS.SUBJECT_WISE_STOCK_REPORT) && (
                    <Col xl={3} md={6}>
                        <Card className={`${styles.reportCard} ${styles.infoCard} border-0 h-100`}>
                            <CardBody className="p-4 d-flex flex-column justify-content-between">
                                <div className="position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-4">
                                        <div className={styles.iconContainer}>
                                            <i className="ri-file-list-line"></i>
                                        </div>
                                        <div className={styles.watermark}>
                                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                                                <circle cx="11" cy="14" r="4" stroke="currentColor" strokeWidth="1.5"/>
                                                <path d="M11 10V14H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <h5 className={styles.cardTitle}>Subject Wise Report</h5>
                                    <p className={styles.cardDesc}>
                                        Detailed report of IP movements, dispensing, and returns tracked per subject.
                                    </p>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div 
                                        className={styles.actionLink}
                                        onClick={() => navigate(REPORTS_PATHS.SUBJECT_WISE)}
                                    >
                                        <i className="ri-file-text-line fs-15 me-1"></i> Generate Report
                                    </div>
                                    <div 
                                        className={styles.arrowCircle}
                                        onClick={() => navigate(REPORTS_PATHS.SUBJECT_WISE)}
                                    >
                                        <i className="ri-arrow-right-line"></i>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    )}
                    </Row>
                    </Container>
                  </>
                )}
        </div>
    );
};

export default SummaryReports;
